/**
 * Synthetic domain seed for the P3 screens (§5–§13). Two event sets — US Bakken
 * (`evt-us1-drl`, imperial vernacular) and Vaca Muerta (`evt-ar1-drl`, metric,
 * SE vocabulary) — matching the existing well/event seed so screen agents have
 * data to wire against. Shapes mirror the P3 wire contracts in @api/types
 * exactly, so MSW is a drop-in stand-in for the future Oxbo-P endpoints.
 *
 * We model the SHAPE of the corpus, never ship real operator values.
 */
import type {
  SurveyStationOut,
  TubularStringOut,
  PipeTallyRowOut,
  BhaRunOut,
  BhaComponentOut,
  BitOpOut,
  MudCheckOut,
  MudProductOut,
  ActivityOut,
  MudTransactionOut,
  MudVolumeOut,
  SafetyDayOut,
  EventSafetyRollupOut,
  AfeLineOut,
  PadRollupOut,
  PadSharedCostLine,
  PadSharedCostsResponse,
  PadCostSplit,
  TimelineNode,
  PlanTimelineResponse,
  CatalogResponse,
} from "@api/types";

const US = "evt-us1-drl";
const US_UWI = "33053024130000";
const AR = "evt-ar1-drl";
const AR_UWI = "AR-NQ-FP-1216";

// ─────────────────────────── Surveys (§5) ───────────────────────────
export const SURVEYS: Record<string, SurveyStationOut[]> = {
  [US_UWI]: [
    s("sv-us-1", US, US_UWI, 17200, 88.1, 184.2, 10310, 640, 210, 1.1, "gyro", "2026-07-16"),
    s("sv-us-2", US, US_UWI, 17500, 88.6, 184.5, 10318, 700, 232, 0.9, "gyro", "2026-07-17"),
    s("sv-us-3", US, US_UWI, 17890, 89.1, 184.9, 10320, 780, 260, 0.8, "gyro", "2026-07-17"),
    s("sv-us-4", US, US_UWI, 18000, 89.4, 185.1, 10321, 812, 271, 0.7, "mwd", "2026-07-18"),
  ],
  [AR_UWI]: [
    s("sv-ar-1", AR, AR_UWI, 2100, 2.1, 96.4, 2098, 12, 40, 0.4, "mwd", "2026-07-16"),
    s("sv-ar-2", AR, AR_UWI, 2300, 3.4, 98.1, 2296, 18, 62, 0.6, "mwd", "2026-07-17"),
    s("sv-ar-3", AR, AR_UWI, 2480, 5.2, 99.7, 2472, 26, 88, 0.9, "mwd", "2026-07-18"),
  ],
};
function s(
  id: string,
  event_id: string,
  well_uwi: string,
  md: number,
  inc: number,
  azi: number,
  tvd: number,
  ns: number,
  ew: number,
  dls: number,
  tool: string,
  date: string,
): SurveyStationOut {
  return {
    id,
    event_id,
    well_uwi,
    md,
    inc,
    azi,
    tvd,
    ns,
    ew,
    dls,
    survey_tool_entry_key: tool,
    date_survey: date,
    carry_over: false,
  };
}

// ─────────────────────────── Tubulars (§6) ───────────────────────────
export const TUBULAR_STRINGS: Record<string, TubularStringOut[]> = {
  [US]: [
    {
      id: "str-us-surf",
      event_id: US,
      well_uwi: US_UWI,
      string_type_entry_key: "SURF",
      nominal_size_entry_key: "9.625",
      grade_entry_key: "J-55",
      connection_entry_key: "BTC",
      shoe_md: 2100,
      shoe_tvd: 2100,
      running: false,
      carry_over: true,
    },
    {
      id: "str-us-int",
      event_id: US,
      well_uwi: US_UWI,
      string_type_entry_key: "INT",
      nominal_size_entry_key: "7",
      grade_entry_key: "L-80",
      connection_entry_key: "BTC",
      shoe_md: 9120,
      shoe_tvd: 9020,
      running: true,
      carry_over: false,
    },
  ],
  [AR]: [
    {
      id: "str-ar-guia",
      event_id: AR,
      well_uwi: AR_UWI,
      string_type_entry_key: "SURF",
      nominal_size_entry_key: "9.625",
      grade_entry_key: "J-55",
      connection_entry_key: "BTC",
      shoe_md: 620,
      shoe_tvd: 620,
      running: false,
      carry_over: true,
    },
  ],
};
export const PIPE_TALLY: Record<string, PipeTallyRowOut[]> = {
  "str-us-int": [
    tally("pt-1", "str-us-int", 1, "SHOE", 0.4, 0.4, "SN-4471"),
    tally("pt-2", "str-us-int", 2, "JOINT", 12.6, 13.0, "SN-4472"),
    tally("pt-3", "str-us-int", 3, "JOINT", 12.5, 25.5, "SN-4473"),
  ],
};
function tally(
  id: string,
  string_id: string,
  joint_no: number,
  comp: string,
  length: number,
  cum: number,
  serial: string,
): PipeTallyRowOut {
  return {
    id,
    string_id,
    joint_no,
    component_type_entry_key: comp,
    length,
    cum_length: cum,
    serial_no: serial,
    centralized: joint_no % 2 === 0 ? "Y" : "N",
  };
}

// ─────────────────────────── BHA (§7) ───────────────────────────
// Three runs on the US well mirror the wireframe: run 1 (surface, PDC + motor),
// run 2 (curve, POOH + dull graded), run 3 (lateral, still in hole). AR carries
// one in-hole run. Each run nests its bottom-up `assembly` + a `bit_ops` grid.
export const BHA_RUNS: Record<string, BhaRunOut[]> = {
  [US]: [
    run("bha-us-1", US, US_UWI, 1, 8120, 12450, 61.4, 70.5, "12.25", "M323", "MB512", "VANTAGE", {
      jets: "16/16/16",
      tfa: 0.74,
      dull: "1-1-WT-A-X-I-NO-BHA",
      pull: "Section TD — casing point.",
      in_hole: false,
      carry_over: false,
    }),
    run("bha-us-2", US, US_UWI, 2, 12450, 18199, 52.1, 110.3, "8.75", "M413", "MB613", "HALBRITE", {
      jets: "14/14/16",
      tfa: 0.62,
      dull: "2-2-WT-A-X-I-NO-TD",
      pull: "Pulled for TD change / directional program — graded fit-for-return by vendor.",
      in_hole: false,
      carry_over: false,
    }),
    run("bha-us-3", US, US_UWI, 3, 18199, null, 38.6, 184.0, "6.125", "M433", "MB713", "HALBRITE", {
      jets: "12/12/13",
      tfa: 0.48,
      dull: null,
      pull: null,
      in_hole: true,
      carry_over: true,
    }),
  ],
  [AR]: [
    run("bha-ar-3", AR, AR_UWI, 3, 2100, null, 38.6, 22.4, "8.5", "S123", "SB421", "VANTAGE", {
      jets: "15/15/15",
      tfa: 0.69,
      dull: null,
      pull: null,
      in_hole: true,
      carry_over: true,
    }),
  ],
};

/** Bottom-up assembly per run (position 1 = bit). Mirrors the wireframe stack. */
export const BHA_COMPONENTS: Record<string, BhaComponentOut[]> = {
  "bha-us-2": [
    comp("bc2-1", "bha-us-2", 1, "BIT", 8.75, null, 0.9, "HALBRITE"),
    comp("bc2-2", "bha-us-2", 2, "BITSUB", 8.0, 2.8, 3.1, "CORDELL"),
    comp("bc2-3", "bha-us-2", 3, "RSS", 8.25, 2.8, 31.4, "VANTAGE"),
    comp("bc2-4", "bha-us-2", 4, "MWD", 8.0, 2.8, 28.6, "VANTAGE"),
    comp("bc2-5", "bha-us-2", 5, "STAB", 8.5, 2.8, 5.2, "CORDELL"),
    comp("bc2-6", "bha-us-2", 6, "NMDC", 8.0, 2.8, 62.0, "CORDELL"),
    comp("bc2-7", "bha-us-2", 7, "XOVER", 7.75, 2.8, 2.8, "CORDELL"),
    comp("bc2-8", "bha-us-2", 8, "HWDP", 6.62, 4.0, 270.0, "VANTAGE"),
  ],
  "bha-us-3": [
    comp("bc3-1", "bha-us-3", 1, "BIT", 6.125, null, 0.8, "HALBRITE"),
    comp("bc3-2", "bha-us-3", 2, "NBSTAB", 6.0, 2.25, 3.0, "VANTAGE"),
    comp("bc3-3", "bha-us-3", 3, "MOTOR", 6.25, 2.25, 29.8, "VANTAGE"),
    comp("bc3-4", "bha-us-3", 4, "MWD", 6.0, 2.25, 28.6, "VANTAGE"),
    comp("bc3-5", "bha-us-3", 5, "STAB", 6.375, 2.25, 5.2, "CORDELL"),
    comp("bc3-6", "bha-us-3", 6, "NMDC", 6.0, 2.25, 62.0, "CORDELL"),
    comp("bc3-7", "bha-us-3", 7, "JAR", 6.75, 2.75, 31.0, "CORDELL"),
    comp("bc3-8", "bha-us-3", 8, "HWDP", 6.62, 4.0, 270.0, "VANTAGE"),
  ],
  "bha-ar-3": [
    comp("bcar3-1", "bha-ar-3", 1, "BIT", 8.5, null, 1.0, "VANTAGE"),
    comp("bcar3-2", "bha-ar-3", 2, "MOTOR", 6.75, 2.0, 28.4, "VANTAGE"),
    comp("bcar3-3", "bha-ar-3", 3, "MWD", 6.75, 2.0, 9.8, "VANTAGE"),
  ],
};

/** On-bottom drilling-parameter samples per run — the bit-ops grid. */
export const BIT_OPS: Record<string, BitOpOut[]> = {
  "bha-us-3": [
    op("bo3-1", "bha-us-3", 18199, 96, 28, 62, 640),
    op("bo3-2", "bha-us-3", 18700, 128, 30, 65, 655),
    op("bo3-3", "bha-us-3", 19100, 152, 32, 68, 660),
    op("bo3-4", "bha-us-3", 19550, 171, 34, 70, 660),
    op("bo3-5", "bha-us-3", 19950, 198, 35, 72, 665),
    op("bo3-6", "bha-us-3", 20041, 184, 34, 71, 662),
  ],
  "bha-us-2": [
    op("bo2-1", "bha-us-2", 12450, 88, 30, 118, 720),
    op("bo2-2", "bha-us-2", 14200, 104, 34, 122, 735),
    op("bo2-3", "bha-us-2", 16100, 121, 36, 126, 740),
    op("bo2-4", "bha-us-2", 18199, 132, 38, 128, 742),
  ],
  "bha-ar-3": [
    op("boar3-1", "bha-ar-3", 2100, 18, 12, 92, 480),
    op("boar3-2", "bha-ar-3", 2300, 26, 14, 96, 495),
    op("boar3-3", "bha-ar-3", 2480, 31, 15, 98, 500),
  ],
};

function run(
  id: string,
  event_id: string,
  well_uwi: string,
  run_no: number,
  md_in: number | null,
  md_out: number | null,
  hours: number | null,
  rop_avg: number | null,
  bit_size: string | null,
  iadc: string | null,
  bit_type: string | null,
  vendor: string | null,
  extra: {
    jets: string | null;
    tfa: number | null;
    dull: string | null;
    pull: string | null;
    in_hole: boolean;
    carry_over: boolean;
  },
): BhaRunOut {
  return {
    id,
    event_id,
    well_uwi,
    run_no,
    md_in,
    md_out,
    hours,
    rop_avg,
    bit_size_entry_key: bit_size,
    bit_iadc: iadc,
    bit_jets: extra.jets,
    bit_tfa: extra.tfa,
    bit_type_entry_key: bit_type,
    bit_vendor_entry_key: vendor,
    dull_grade: extra.dull,
    pull_reason: extra.pull,
    in_hole: extra.in_hole,
    carry_over: extra.carry_over,
  };
}

function comp(
  id: string,
  bha_run_id: string,
  position: number,
  ctype: string,
  od: number,
  idn: number | null,
  length: number,
  vendor: string | null,
): BhaComponentOut {
  return {
    id,
    bha_run_id,
    position,
    component_type_entry_key: ctype,
    od_in: od,
    id_in: idn,
    length,
    vendor_entry_key: vendor,
  };
}

function op(
  id: string,
  bha_run_id: string,
  md: number,
  rop: number | null,
  wob: number | null,
  rpm: number | null,
  flow_rate: number | null,
): BitOpOut {
  return { id, bha_run_id, md, rop, wob, rpm, flow_rate };
}

// ─────────────────────────── Mud (§8) ───────────────────────────
export const MUD_CHECKS: Record<string, MudCheckOut[]> = {
  [US]: [
    mud("mc-us-1", "ddr-us1-13", US, "06:00", 17890, 10.3, 18, 22, 9.4, 28000),
    mud("mc-us-2", "ddr-us1-14", US, "06:00", 18450, 10.4, 19, 23, 9.5, 29000),
    mud("mc-us-3", "ddr-us1-14", US, "18:00", 18450, 10.4, 20, 24, 9.5, 29500),
  ],
  [AR]: [mud("mc-ar-1", "ddr-ar1-11", AR, "06:00", 2480, 1.25, 16, 20, 9.1, 12000)],
};
function mud(
  id: string,
  report_id: string,
  event_id: string,
  time: string,
  depth: number,
  mw: number,
  pv: number,
  yp: number,
  ph: number,
  cl: number,
): MudCheckOut {
  return {
    id,
    report_id,
    event_id,
    check_time: time,
    check_depth: depth,
    mw,
    pv,
    yp,
    ph,
    chlorides: cl,
    carry_over: false,
  };
}
/** Daily time-log activities (§ time-entry), keyed by report_id. */
const activity = (o: Partial<ActivityOut> & Pick<ActivityOut, "id" | "sequence_no">): ActivityOut => ({
  entity_id: o.id, // seed: entity_id mirrors the version-row id (single version)
  version: 1,
  status: "active",
  daily_report_id: "ddr-us1-14",
  event_id: US,
  well_uwi: "33053024130000",
  wellbore_id: null,
  phase_entry_key: "Lateral",
  activity_code_entry_key: null,
  activity_subcode_entry_key: null,
  op_code_key: null,
  service_company_entry_key: null,
  time_from: null,
  time_to: null,
  duration_hours: null,
  md_from: null,
  md_to: null,
  time_class: "productive",
  npt_level: "0",
  npt_class_entry_key: null,
  npt_subclass_entry_key: null,
  npt_reason_key: null,
  activity_memo: null,
  change_reason: null,
  ...o,
});

export const ACTIVITIES: Record<string, ActivityOut[]> = {
  "ddr-us1-14": [
    activity({
      id: "act-us1-14-1",
      sequence_no: 1,
      activity_code_entry_key: "DRLG",
      time_from: "06:00",
      time_to: "12:00",
      duration_hours: 6,
      md_from: 18000,
      md_to: 18450,
      activity_memo: "Drilling lateral, 8½in section.",
    }),
    activity({
      id: "act-us1-14-2",
      sequence_no: 2,
      activity_code_entry_key: "CONN",
      time_from: "12:00",
      time_to: "13:00",
      duration_hours: 1,
      md_from: 18450,
      md_to: 18450,
      activity_memo: "Connections.",
    }),
    activity({
      id: "act-us1-14-3",
      sequence_no: 3,
      activity_code_entry_key: "REPAIR",
      time_from: "13:00",
      time_to: "14:30",
      duration_hours: 1.5,
      md_from: 18450,
      md_to: 18450,
      time_class: "npt",
      npt_level: "1",
      npt_class_entry_key: "NPT-MUD",
      npt_reason_key: "EQUIPMENT",
      activity_memo: "Top-drive hydraulic leak — repaired.",
    }),
  ],
};

export const MUD_PRODUCTS: Record<string, MudProductOut[]> = {
  "ddr-us1-14": [
    {
      id: "mp-1",
      report_id: "ddr-us1-14",
      product_entry_key: "BARITE",
      qty: 120,
      unit: "sk",
      unit_cost: 30,
      cost: 3600,
      carry_over: true,
    },
    {
      id: "mp-2",
      report_id: "ddr-us1-14",
      product_entry_key: "BENTONITE",
      qty: 40,
      unit: "sk",
      unit_cost: 22.5,
      cost: 900,
      carry_over: false,
    },
    {
      id: "mp-3",
      report_id: "ddr-us1-14",
      product_entry_key: "CAUSTIC",
      qty: 8,
      unit: "sk",
      unit_cost: 22,
      cost: 176,
      carry_over: false,
    },
    {
      id: "mp-4",
      report_id: "ddr-us1-14",
      product_entry_key: "LCM",
      qty: 20,
      unit: "sk",
      unit_cost: 18,
      cost: 360,
      carry_over: false,
    },
  ],
  "ddr-ar1-11": [
    {
      id: "mp-ar-1",
      report_id: "ddr-ar1-11",
      product_entry_key: "BARITE",
      qty: 90,
      unit: "sk",
      unit_cost: 28,
      cost: 2520,
      carry_over: true,
    },
  ],
};

// Non-product mud transactions (build / dump / transfer), keyed by report.
export const MUD_TRANSACTIONS: Record<string, MudTransactionOut[]> = {
  "ddr-us1-14": [
    {
      id: "mt-1",
      report_id: "ddr-us1-14",
      kind: "build",
      qty: 65,
      unit: "bbl",
      note: "New system volume built",
    },
    {
      id: "mt-2",
      report_id: "ddr-us1-14",
      kind: "dump",
      qty: 20,
      unit: "bbl",
      note: "Excess pit dumped",
    },
  ],
};

// A report's mud volumes — one record per day, keyed by report.
export const MUD_VOLUMES: Record<string, MudVolumeOut> = {
  "ddr-us1-14": {
    id: "mv-us1-14",
    report_id: "ddr-us1-14",
    event_id: US,
    active: 1240,
    reserve: 380,
    built: 65,
    dumped: 20,
    loss_surface: 6,
    loss_downhole: 9,
    unit: "bbl",
    carry_over: false,
  },
  "ddr-ar1-11": {
    id: "mv-ar1-11",
    report_id: "ddr-ar1-11",
    event_id: AR,
    active: 197,
    reserve: 60,
    built: 10,
    dumped: 3,
    loss_surface: 1,
    loss_downhole: 2,
    unit: "m³",
    carry_over: false,
  },
};

// ─────────────────────────── HSE (§9) ───────────────────────────
// Safety is a DAILY HEADER: one row per daily report (LTI counters + the day's
// well-control / compliance checks + people/incident tallies) — NOT a per-
// observation open→close action list (that's a later schema add). The event
// endpoint returns the current LTI counters + the daily HSE trail.

/** Per-report daily HSE header (wire `SafetyOut` shape), keyed by report_id. */
export const SAFETY_DAYS: Record<string, SafetyDayOut> = {
  "ddr-us1-14": {
    id: "sfty-us1-14",
    daily_report_id: "ddr-us1-14",
    event_id: US,
    well_uwi: US_UWI,
    date_report: "2026-07-18",
    days_since_lti_operator: 142,
    days_since_lti_total: 142,
    rid_cards: 6,
    job_safety_analysis: 4,
    jobsite_evaluation: 2,
    bop_pressure_annular: 1500,
    last_casing_test_pressure: 3200,
    is_company_inspected: true,
    is_government_inspected: false,
  },
  "ddr-us1-13": {
    id: "sfty-us1-13",
    daily_report_id: "ddr-us1-13",
    event_id: US,
    well_uwi: US_UWI,
    date_report: "2026-07-17",
    days_since_lti_operator: 141,
    days_since_lti_total: 141,
    rid_cards: 5,
    job_safety_analysis: 3,
    jobsite_evaluation: 1,
    bop_pressure_annular: 1500,
    last_casing_test_pressure: null,
    is_company_inspected: true,
    is_government_inspected: false,
  },
  "ddr-ar1-11": {
    id: "sfty-ar1-11",
    daily_report_id: "ddr-ar1-11",
    event_id: AR,
    well_uwi: AR_UWI,
    date_report: "2026-07-18",
    days_since_lti_operator: 88,
    days_since_lti_total: 210,
    rid_cards: 4,
    job_safety_analysis: 2,
    jobsite_evaluation: 1,
    bop_pressure_annular: 1400,
    last_casing_test_pressure: 3000,
    is_company_inspected: true,
    is_government_inspected: true,
  },
};

/** The daily HSE trail for an event — its days (wire records), newest first. */
function trailFor(eventId: string): EventSafetyRollupOut["items"] {
  return Object.values(SAFETY_DAYS)
    .filter((d) => d.event_id === eventId)
    .sort((a, b) => (b.date_report ?? "").localeCompare(a.date_report ?? ""));
}

/** Per-event LTI rollup + daily HSE trail (newest first), keyed by event_id. */
export const SAFETY_ROLLUPS: Record<string, EventSafetyRollupOut> = {
  [US]: {
    event_id: US,
    days_since_lti_operator: 142,
    days_since_lti_total: 142,
    items: trailFor(US),
  },
  [AR]: {
    event_id: AR,
    days_since_lti_operator: 88,
    days_since_lti_total: 210,
    items: trailFor(AR),
  },
};

// ─────────────────────────── AFE lines (§11) ───────────────────────────
export const AFE_LINES: Record<string, AfeLineOut[]> = {
  [US]: [
    afe(
      "afe-us-1",
      US,
      "01-100",
      "Rig day-rate",
      "Lateral",
      "by_quantity",
      "day",
      14,
      32000,
      448000,
      448000,
      448000,
      448000,
      "approved",
      { owner: "operator" },
    ),
    afe(
      "afe-us-2",
      US,
      "02-210",
      "Directional services",
      "Lateral",
      "by_quantity",
      "day",
      12,
      18500,
      240000,
      222000,
      222000,
      210400,
      "approved",
      { owner: "service" },
    ),
    afe(
      "afe-us-3",
      US,
      "03-400",
      "Mobilization",
      "Mob / demob",
      "lump",
      "ls",
      1,
      350000,
      350000,
      350000,
      350000,
      350000,
      "approved",
      { owner: "operator" },
    ),
    afe(
      "afe-us-4",
      US,
      "04-520",
      "Casing — intermediate",
      "Intermediate",
      "milestone",
      "ls",
      1,
      684000,
      684000,
      500000,
      500000,
      500000,
      "pending",
      { owner: "operator" },
    ),
    afe(
      "afe-us-5",
      US,
      "02-210",
      "Directional rate escalation — extended lateral",
      "Lateral",
      "by_quantity",
      "ft",
      0,
      92.5,
      185000,
      0,
      0,
      0,
      "pending",
      { owner: "service", additional: true },
    ),
  ],
  [AR]: [
    afe(
      "afe-ar-1",
      AR,
      "01-100",
      "Tarifa diaria equipo",
      "Intermedia",
      "by_quantity",
      "día",
      11,
      24000,
      264000,
      264000,
      264000,
      264000,
      "approved",
      { owner: "operator", currency: "USD" },
    ),
    afe(
      "afe-ar-2",
      AR,
      "02-210",
      "Servicios direccionales",
      "Intermedia",
      "by_quantity",
      "día",
      9,
      15000,
      135000,
      120000,
      120000,
      108000,
      "approved",
      { owner: "service", currency: "USD" },
    ),
  ],
};
function afe(
  id: string,
  event_id: string,
  account: string,
  desc: string,
  step: string,
  method: AfeLineOut["accrual_method"],
  unit: string,
  qty: number,
  rate: number,
  budget: number,
  committed: number,
  accrued: number,
  actual: number,
  status: string,
  opts?: {
    owner?: AfeLineOut["owner_party"];
    additional?: boolean;
    currency?: string;
  },
): AfeLineOut {
  return {
    id,
    event_id,
    account_entry_key: account,
    description: desc,
    program_step: step,
    accrual_method: method,
    unit,
    qty,
    rate,
    budget,
    committed,
    accrued,
    actual,
    status,
    currency: opts?.currency ?? "USD",
    owner_party: opts?.owner ?? "operator",
    is_additional: opts?.additional ?? false,
  };
}

// ─────────────────────────── Pad rollup (§12) ───────────────────────────
export const PAD_ROLLUPS: Record<string, PadRollupOut> = {
  "site-nesson": {
    site_id: "site-nesson",
    pad_name: "Nesson 24-13 Pad",
    field: "Nesson Anticline",
    well_count: 3,
    pad_total_cost: 14820000,
    shared_cost: 1650000,
    pad_days: 41,
    rig_utilization_pct: 78,
    currency: "USD",
    per_well: [
      {
        well_uwi: US_UWI,
        common_name: "Nesson 24-13H",
        direct_cost: 8420000,
        allocated_shared: 620000,
        total_cost: 9040000,
        pct_of_pad: 61,
        days: 18,
      },
      {
        well_uwi: "33061008050000",
        common_name: "Three Forks 8-5H",
        direct_cost: 3210000,
        allocated_shared: 540000,
        total_cost: 3750000,
        pct_of_pad: 25,
        days: 13,
      },
      {
        well_uwi: "33025001110000",
        common_name: "Bakken State 1-11",
        direct_cost: 1540000,
        allocated_shared: 490000,
        total_cost: 2030000,
        pct_of_pad: 14,
        days: 10,
      },
    ],
    shared_lines: [
      {
        id: "sc-1",
        cost_class_entry_key: "mobilization",
        description: "Rig mob to pad",
        vendor_entry_key: "H&P FlexRig 447",
        amount: 620000,
        allocation_method: "equal",
      },
      {
        id: "sc-2",
        cost_class_entry_key: "pad_construction",
        description: "Pad build + access road",
        vendor_entry_key: "Basin Trucking LLC",
        amount: 740000,
        allocation_method: "footage",
      },
      {
        id: "sc-3",
        cost_class_entry_key: "shared_services",
        description: "Shared water handling",
        vendor_entry_key: "MudTech Fluids",
        amount: 290000,
        allocation_method: "rig_days",
      },
    ],
  },
};

// ── Shared/pad cost lines (§12) — the server-computed allocation ──────────────
// The pad's per-well drivers (footage, rig_days, afe_weight) that the six
// allocation methods split a shared line across. Keyed by site.
const PAD_WELL_DRIVERS: Record<
  string,
  { well_uwi: string; common_name: string; footage: number; rig_days: number; afe_weight: number }[]
> = {
  "site-nesson": [
    { well_uwi: US_UWI, common_name: "Nesson 24-13H", footage: 20300, rig_days: 18, afe_weight: 61 },
    { well_uwi: "33061008050000", common_name: "Three Forks 8-5H", footage: 18900, rig_days: 13, afe_weight: 25 },
    { well_uwi: "33025001110000", common_name: "Bakken State 1-11", footage: 9800, rig_days: 10, afe_weight: 14 },
  ],
};

/**
 * Compute a shared line's per-well split (server-side in the real backend; here
 * we mirror the six allocation methods so the mock returns the same shape). The
 * FE only displays this — it never recomputes an allocation.
 */
export function computeMockSplit(site: string, line: PadSharedCostLine): PadCostSplit[] {
  const drivers = PAD_WELL_DRIVERS[site] ?? [];
  if (drivers.length === 0) return [];
  const weightOf = (d: (typeof drivers)[number]): number => {
    switch (line.allocation_method) {
      case "equal":
      case "well_count":
        return 1;
      case "footage":
        return d.footage;
      case "rig_days":
        return d.rig_days;
      case "afe_weight":
        return d.afe_weight;
      case "manual_pct":
        // manual_pct carries operator percentages on the line's split already;
        // fall back to equal if none were supplied.
        return 1;
    }
  };
  const total = drivers.reduce((s, d) => s + weightOf(d), 0) || 1;
  return drivers.map((d) => {
    const pct = (weightOf(d) / total) * 100;
    return {
      well_uwi: d.well_uwi,
      common_name: d.common_name,
      amount: Math.round((line.amount * pct) / 100),
      pct: Math.round(pct * 10) / 10,
    };
  });
}

/** Attach computed splits to the pad's shared lines (from PAD_ROLLUPS). */
function sharedCostsFor(site: string): PadSharedCostsResponse {
  const roll = PAD_ROLLUPS[site];
  const lines = (roll?.shared_lines ?? []).map((l) => ({ ...l, split: computeMockSplit(site, l) }));
  return { site_id: site, currency: roll?.currency ?? "USD", lines };
}

// Mutable per-site store so POST/PATCH round-trip in standalone mode.
export const PAD_SHARED_COSTS: Record<string, PadSharedCostsResponse> = Object.fromEntries(
  Object.keys(PAD_ROLLUPS).map((site) => [site, sharedCostsFor(site)]),
);

// ─────────────────────────── Timeline nodes (§16e) ───────────────────────────
export const TIMELINE_NODES: Record<string, TimelineNode[]> = {
  [US]: [
    node(
      "nd-us-surf",
      US,
      "section",
      "Surface hole",
      0,
      2100,
      "2026-07-04",
      "2026-07-06",
      "2026-07-04",
      "2026-07-06",
      "complete",
    ),
    node(
      "nd-us-int",
      US,
      "section",
      "Intermediate",
      2100,
      9120,
      "2026-07-06",
      "2026-07-10",
      "2026-07-06",
      "2026-07-11",
      "complete",
    ),
    node(
      "nd-us-curve",
      US,
      "section",
      "Curve",
      9120,
      11200,
      "2026-07-11",
      "2026-07-13",
      "2026-07-11",
      "2026-07-13",
      "complete",
    ),
    node(
      "nd-us-lat",
      US,
      "section",
      "Lateral",
      11200,
      20300,
      "2026-07-13",
      "2026-07-20",
      "2026-07-13",
      null,
      "active",
    ),
  ],
  [AR]: [
    node(
      "nd-ar-guia",
      AR,
      "section",
      "Guía",
      0,
      620,
      "2026-07-07",
      "2026-07-08",
      "2026-07-07",
      "2026-07-08",
      "complete",
    ),
    node(
      "nd-ar-int1",
      AR,
      "section",
      "1 Intermedia",
      620,
      1800,
      "2026-07-08",
      "2026-07-14",
      "2026-07-08",
      "2026-07-15",
      "complete",
    ),
    node(
      "nd-ar-int2",
      AR,
      "section",
      "2 Intermedia",
      1800,
      2700,
      "2026-07-15",
      "2026-07-19",
      "2026-07-15",
      null,
      "active",
    ),
  ],
};
function node(
  external_id: string,
  event_id: string,
  kind: TimelineNode["kind"],
  label: string,
  df: number,
  dt: number,
  ps: string,
  pe: string,
  as_: string,
  ae: string | null,
  status: string,
): TimelineNode {
  return {
    external_id,
    event_id,
    kind,
    label,
    depth_from: df,
    depth_to: dt,
    planned_start: ps,
    planned_end: pe,
    actual_start: as_,
    actual_end: ae,
    status,
    detail_ref: `section:${external_id}`,
  };
}

// ─────────────────────────── Plan timeline (§13/§13a/§19) ───────────────────
// Multi-track plan+actual per hierarchy level (Fleet → Pad → Well → Section).
// The CPE track (rig/frac/CT) is the spine; wells sequence along it; offline ops
// live on the adjacent well's track. Keyed by scope: "fleet" for the portfolio
// view, a site_id for a pad, a UWI for a well, an event_id for a section/ops.
export const PLAN_TIMELINES: Record<string, PlanTimelineResponse> = {
  // Fleet level (§13a) — one track per CPE unit across all pads over a horizon.
  fleet: {
    level: "fleet",
    scope: "fleet",
    tracks: [
      {
        id: "trk-rig-precision",
        label: "Rig · Precision-482",
        kind: "rig",
        bars: [
          bar("f-rig1-a", "Redhawk Pad — 4 wells", "drilling", "2026-05-04", "2026-06-16", "2026-05-04", "2026-06-14", false, false),
          bar("f-rig1-mv", "Rig move", "pad", "2026-06-14", "2026-06-20", "2026-06-14", "2026-06-20", false, false),
          bar("f-rig1-b", "Coyote Draw Pad — 3 wells", "drilling", "2026-06-20", "2026-08-02", null, null, false, false),
        ],
      },
      {
        id: "trk-rig-nabors",
        label: "Rig · Nabors-127",
        kind: "rig",
        bars: [
          bar("f-rig2-c", "Antelope Pad — 3 wells", "drilling", "2026-05-04", "2026-07-01", "2026-05-04", null, false, false),
        ],
      },
      {
        id: "trk-frac-alpha",
        label: "Frac spread · Alpha",
        kind: "frac",
        bars: [
          bar("f-frac-standby", "Standby", "pad", "2026-05-04", "2026-06-18", "2026-05-04", "2026-06-18", false, false),
          bar("f-frac-a", "Redhawk Pad — completion", "completion", "2026-06-18", "2026-07-10", "2026-06-19", null, false, false),
        ],
      },
      {
        id: "trk-ct-01",
        label: "CT unit · CTU-01",
        kind: "ct",
        bars: [
          bar("f-ct-a", "Redhawk Pad — TPO", "ct", "2026-07-12", "2026-07-26", null, null, false, false),
        ],
      },
    ],
  },

  // Pad level (§13) — CPE spine (rig walks well-to-well) + one track per well.
  "site-nesson": {
    level: "pad",
    scope: "site-nesson",
    tracks: [
      {
        id: "trk-rig",
        label: "Rig — H&P FlexRig 447",
        kind: "rig",
        bars: [
          bar(
            "b-r1",
            "Nesson 24-13H",
            "drilling",
            "2026-07-04",
            "2026-07-20",
            "2026-07-04",
            null,
            false,
            false,
          ),
          bar(
            "b-r1-mv",
            "Rig move",
            "pad",
            "2026-07-20",
            "2026-07-21",
            "2026-07-20",
            null,
            false,
            false,
          ),
          bar(
            "b-r2",
            "Three Forks 8-5H",
            "drilling",
            "2026-07-21",
            "2026-08-02",
            null,
            null,
            false,
            false,
          ),
        ],
      },
      {
        id: "trk-w1",
        label: "Nesson 24-13H",
        kind: "well",
        bars: [
          bar(
            "b-w1a",
            "Surface",
            "drilling",
            "2026-07-04",
            "2026-07-06",
            "2026-07-04",
            "2026-07-06",
            false,
            false,
          ),
          bar(
            "b-w1b",
            "Intermediate",
            "drilling",
            "2026-07-06",
            "2026-07-11",
            "2026-07-06",
            "2026-07-11",
            false,
            false,
          ),
          bar(
            "b-w1c",
            "Lateral",
            "drilling",
            "2026-07-13",
            "2026-07-20",
            "2026-07-13",
            null,
            false,
            false,
          ),
          bar(
            "b-w1npt",
            "Downhole NPT",
            "npt",
            "2026-07-15",
            "2026-07-16",
            "2026-07-15",
            "2026-07-16",
            true,
            false,
          ),
        ],
      },
      {
        id: "trk-w2",
        label: "Three Forks 8-5H",
        kind: "well",
        bars: [
          // Offline rig-up on the next well while the rig drills Nesson 24-13H.
          bar("b-w2ru", "Rig-up (offline)", "drilling", "2026-07-18", "2026-07-21", "2026-07-18", "2026-07-20", false, true),
          bar("b-w2a", "Surface", "drilling", "2026-07-21", "2026-07-23", null, null, false, false),
          bar("b-w2b", "Intermediate", "drilling", "2026-07-23", "2026-07-28", null, null, false, false),
        ],
      },
      {
        id: "trk-frac",
        label: "Frac spread",
        kind: "frac",
        bars: [
          bar(
            "b-f1",
            "Nesson 24-13H frac",
            "completion",
            "2026-07-24",
            "2026-08-01",
            null,
            null,
            false,
            false,
          ),
        ],
      },
    ],
  },

  // Well level (§13/§16c) — drilling sub-phases (incl. pilot hole) + support.
  [US_UWI]: {
    level: "well",
    scope: US_UWI,
    tracks: [
      {
        id: "trk-phase",
        label: "Hole sections",
        kind: "well",
        bars: [
          bar("w-pilot", "Pilot hole", "pilot", "2026-07-04", "2026-07-08", "2026-07-04", "2026-07-08", false, false),
          bar("w-surf", "Surface", "drilling", "2026-07-08", "2026-07-10", "2026-07-08", "2026-07-10", false, false),
          bar("w-int", "Intermediate", "drilling", "2026-07-10", "2026-07-13", "2026-07-10", "2026-07-13", false, false),
          bar("w-curve", "Curve", "drilling", "2026-07-13", "2026-07-15", "2026-07-13", "2026-07-15", false, false),
          bar("w-lat", "Lateral", "drilling", "2026-07-15", "2026-07-20", "2026-07-15", null, false, false),
          bar("w-npt", "Stuck pipe", "npt", "2026-07-16", "2026-07-17", "2026-07-16", "2026-07-17", true, false),
        ],
      },
      {
        id: "trk-casing",
        label: "Casing runs",
        kind: "casing",
        bars: [
          bar("w-cs-surf", "Surface casing", "casing", "2026-07-10", "2026-07-10", "2026-07-10", "2026-07-10", false, false),
          bar("w-cs-int", "Intermediate casing", "casing", "2026-07-13", "2026-07-13", "2026-07-13", "2026-07-13", false, false),
        ],
      },
      {
        id: "trk-frac-w",
        label: "Completion",
        kind: "frac",
        bars: [bar("w-frac", "40-stage frac", "completion", "2026-07-24", "2026-08-01", null, null, false, false)],
      },
    ],
  },

  // Event / section level (§13/§16e) — detailed ops within the lateral section.
  [US]: {
    level: "event",
    scope: US,
    tracks: [
      {
        id: "trk-drill",
        label: "Drill",
        kind: "section",
        bars: [
          bar("e-d1", "Drill stand 1", "drilling", "2026-07-15T00:00", "2026-07-15T10:00", "2026-07-15T00:00", "2026-07-15T11:00", false, false),
          bar("e-d2", "Drill stand 2", "drilling", "2026-07-15T12:00", "2026-07-15T22:00", "2026-07-15T13:00", "2026-07-15T23:00", false, false),
        ],
      },
      {
        id: "trk-conn",
        label: "Connections",
        kind: "section",
        bars: [
          bar("e-c1", "Connection", "drilling", "2026-07-15T10:00", "2026-07-15T12:00", "2026-07-15T11:00", "2026-07-15T13:00", false, false),
        ],
      },
      {
        id: "trk-npt",
        label: "NPT",
        kind: "section",
        bars: [
          bar("e-npt", "Stuck pipe", "npt", "2026-07-16T02:00", "2026-07-16T09:00", "2026-07-16T02:00", "2026-07-16T09:00", true, false),
        ],
      },
    ],
  },
};
function bar(
  id: string,
  label: string,
  op: string,
  ps: string,
  pe: string,
  as_: string | null,
  ae: string | null,
  npt: boolean,
  offline: boolean,
): PlanTimelineResponse["tracks"][number]["bars"][number] {
  return {
    id,
    label,
    op_type: op,
    planned_start: ps,
    planned_end: pe,
    actual_start: as_,
    actual_end: ae,
    is_npt: npt,
    is_offline: offline,
  };
}

// ─────────────────── Catalog value-API (§22 / §23) ───────────────────
// The picker-facing catalogs. Each carries the §23b value envelope; the
// `admin` block here is what the server would send to an AUTHORISED caller —
// the handler STRIPS it for non-admin requests (§22c) so it never reaches a
// field user's client.
function cv(code: string, label: string, extra?: Record<string, unknown>) {
  return { code, label, active: true, ...extra };
}

export const CATALOG_VALUES: Record<string, CatalogResponse> = {
  string_types: {
    key: "string_types",
    label: "String types",
    shape: "flat",
    editable: true,
    version: "2026-07-01",
    values: [
      cv("COND", "Conductor"),
      cv("SURF", "Surface casing"),
      cv("INT", "Intermediate casing"),
      cv("PROD", "Production casing"),
      cv("LINER", "Liner"),
    ],
    admin: {
      url: "https://m.dev.oxbo.energy/catalogs/string_types",
      capability: "catalog:string_types:write",
    },
  },
  grades: {
    key: "grades",
    label: "Pipe grades",
    shape: "flat",
    editable: true,
    version: "2026-07-01",
    values: [
      cv("J-55", "API J-55", { aliases: ["J55"] }),
      cv("L-80", "API L-80", { aliases: ["L80"] }),
      cv("N-80", "API N-80"),
      cv("P-110", "API P-110", { aliases: ["P110"] }),
    ],
    admin: { url: "https://m.dev.oxbo.energy/catalogs/grades", capability: "catalog:grades:write" },
  },
  connections: {
    key: "connections",
    label: "Connections",
    shape: "flat",
    editable: true,
    values: [
      cv("BTC", "Buttress (BTC)"),
      cv("LTC", "Long round (LTC)"),
      cv("VAM", "VAM TOP"),
      cv("TSH", "TenarisHydril"),
    ],
    admin: {
      url: "https://m.dev.oxbo.energy/catalogs/connections",
      capability: "catalog:connections:write",
    },
  },
  nominal_sizes: {
    key: "nominal_sizes",
    label: "OD / hole sizes",
    shape: "attributed",
    editable: true,
    values: [
      cv("12.25", '12¼″', { attrs: { od_in: 12.25 } }),
      cv("9.625", '9⅝″', { attrs: { od_in: 9.625 } }),
      cv("8.75", '8¾″', { attrs: { od_in: 8.75 } }),
      cv("8.5", '8½″', { attrs: { od_in: 8.5 } }),
      cv("7", '7″', { attrs: { od_in: 7 } }),
      cv("6.125", '6⅛″', { attrs: { od_in: 6.125 } }),
    ],
    admin: {
      url: "https://m.dev.oxbo.energy/catalogs/nominal_sizes",
      capability: "catalog:nominal_sizes:write",
    },
  },
  component_types: {
    key: "component_types",
    label: "Tubular / BHA component types",
    shape: "flat",
    editable: true,
    values: [
      cv("BIT", "PDC bit"),
      cv("BITSUB", "Bit sub"),
      cv("MOTOR", "Mud motor"),
      cv("RSS", "Rotary steerable (RSS)"),
      cv("MWD", "MWD / LWD collar"),
      cv("STAB", "String stabilizer"),
      cv("NBSTAB", "Near-bit stabilizer"),
      cv("NMDC", "Non-mag drill collar"),
      cv("XOVER", "Crossover sub"),
      cv("JAR", "Drilling jar"),
      cv("HWDP", "Heavy-weight drill pipe"),
      cv("SHOE", "Float shoe"),
      cv("JOINT", "Casing joint"),
    ],
    admin: { url: "https://m.dev.oxbo.energy/catalogs/component_types", capability: "catalog:component_types:write" },
  },
  bit_types: {
    key: "bit_types",
    label: "Bit types (make / IADC)",
    shape: "attributed",
    editable: true,
    values: [
      cv("MB512", "5-blade PDC · IADC M323", { attrs: { blades: 5, iadc: "M323" } }),
      cv("MB613", "6-blade PDC · IADC M413", { attrs: { blades: 6, iadc: "M413" } }),
      cv("MB713", "6-blade PDC · IADC M433", { attrs: { blades: 6, iadc: "M433" } }),
      cv("SB421", "5-blade PDC · IADC S123", { attrs: { blades: 5, iadc: "S123" } }),
    ],
    admin: { url: "https://m.dev.oxbo.energy/catalogs/bit_types", capability: "catalog:bit_types:write" },
  },
  vendors: {
    key: "vendors",
    label: "Service vendors",
    shape: "flat",
    editable: true,
    values: [
      cv("HALBRITE", "Halbrite Bits"),
      cv("CORDELL", "Cordell Tools"),
      cv("VANTAGE", "Vantage Directional"),
    ],
    admin: { url: "https://m.dev.oxbo.energy/catalogs/vendors", capability: "catalog:vendors:write" },
  },
  activity_codes: {
    key: "activity_codes",
    label: "Activity / step codes",
    shape: "attributed",
    editable: true,
    values: [
      cv("DRLF", "Drilling — rotating", { attrs: { is_productive: true } }),
      cv("DRLS", "Drilling — sliding", { attrs: { is_productive: true } }),
      cv("TRIP", "Tripping", { attrs: { is_productive: true } }),
      cv("CSG", "Running casing", { attrs: { is_productive: true } }),
      cv("CMT", "Cementing", { attrs: { is_productive: true } }),
      cv("NPT", "Non-productive time", { attrs: { is_productive: false } }),
    ],
    admin: {
      url: "https://m.dev.oxbo.energy/catalogs/activity_codes",
      capability: "catalog:activity_codes:write",
    },
  },
  cost_accounts: {
    key: "cost_accounts",
    label: "Cost accounts",
    shape: "attributed",
    editable: true,
    values: [
      cv("01-100", "Rig day-rate", {
        attrs: { agrupador: "DTM", tratamiento: "Avance", unidad: "day" },
      }),
      cv("02-210", "Directional services", {
        attrs: { agrupador: "Direccional", tratamiento: "Devengado", unidad: "day" },
      }),
      cv("03-400", "Mobilization", {
        attrs: { agrupador: "DTM", tratamiento: "Devengado", unidad: "ls" },
      }),
      cv("04-520", "Casing — intermediate", {
        attrs: { agrupador: "Equipo", tratamiento: "Devengado", unidad: "ls" },
      }),
    ],
    admin: {
      url: "https://m.dev.oxbo.energy/catalogs/cost_accounts",
      capability: "catalog:cost_accounts:write",
    },
  },
  mud_products: {
    key: "mud_products",
    label: "Mud products",
    shape: "attributed",
    editable: true,
    values: [
      cv("BARITE", "Barite", { attrs: { uom: "sk" } }),
      cv("BENTONITE", "Bentonite", { attrs: { uom: "sk" } }),
      cv("CAUSTIC", "Caustic soda", { attrs: { uom: "sk" } }),
      cv("LCM", "Lost-circulation material", { attrs: { uom: "sk" } }),
    ],
    admin: {
      url: "https://m.dev.oxbo.energy/catalogs/mud_products",
      capability: "catalog:mud_products:write",
    },
  },
  hse_categories: {
    key: "hse_categories",
    label: "HSE categories",
    shape: "flat",
    editable: true,
    values: [
      cv("housekeeping", "Housekeeping"),
      cv("ppe", "PPE"),
      cv("well_control", "Well control"),
      cv("dropped_object", "Dropped object"),
    ],
    admin: {
      url: "https://m.dev.oxbo.energy/catalogs/hse_categories",
      capability: "catalog:hse_categories:write",
    },
  },
  survey_tools: {
    key: "survey_tools",
    label: "Survey tools",
    shape: "flat",
    editable: true,
    values: [cv("mwd", "MWD"), cv("gyro", "Gyro"), cv("magnetic", "Magnetic single-shot")],
    // No admin block — demonstrates graceful degradation (§22b external case).
  },
};
